export const BOAT_CLUB_LAUNCH_FUEL_DISCOUNT_PER_GALLON = 0.25;

export function isBoatClubCustomerType(customerType: string | null | undefined): boolean {
  return (customerType ?? '').toUpperCase() === 'BOAT_CLUB';
}

export function getBoatClubLaunchFuelUnitPrice(listPricePerGallon: number): number {
  if (listPricePerGallon <= 0) {
    return 0;
  }
  return Math.max(0, listPricePerGallon - BOAT_CLUB_LAUNCH_FUEL_DISCOUNT_PER_GALLON);
}

export function formatFuelTypeLabel(fuelType: string): string {
  return fuelType.replace(/([A-Z])/g, ' $1').trim();
}

export function buildFuelOptionLabel(
  fuelType: string,
  listPricePerGallon: number,
  applyBoatClubDiscount: boolean
): string {
  const name = formatFuelTypeLabel(fuelType);
  if (listPricePerGallon <= 0) {
    return name;
  }
  if (!applyBoatClubDiscount) {
    return `${name} ($${listPricePerGallon.toFixed(2)}/gal)`;
  }
  const discounted = getBoatClubLaunchFuelUnitPrice(listPricePerGallon);
  return `${name} ($${discounted.toFixed(2)}/gal)`;
}

export interface LaunchFuelEstimateInput {
  listPricePerGallon: number;
  mode: 'GALLONS' | 'DOLLARS' | 'FILL_TO_FULL';
  amount?: number | null;
  tankCapacityGallons?: number | null;
  applyBoatClubDiscount: boolean;
}

export function calculateLaunchFuelEstimate(input: LaunchFuelEstimateInput): number {
  const { listPricePerGallon, mode, amount, tankCapacityGallons, applyBoatClubDiscount } = input;
  if (listPricePerGallon <= 0) {
    return 0;
  }

  const effectivePrice = applyBoatClubDiscount
    ? getBoatClubLaunchFuelUnitPrice(listPricePerGallon)
    : listPricePerGallon;

  if (mode === 'FILL_TO_FULL') {
    if (tankCapacityGallons != null && tankCapacityGallons > 0) {
      return effectivePrice * tankCapacityGallons;
    }
    return 0;
  }

  if (amount != null && amount > 0) {
    if (mode === 'GALLONS') {
      return effectivePrice * amount;
    }
    if (applyBoatClubDiscount) {
      const gallons = amount / listPricePerGallon;
      return gallons * effectivePrice;
    }
    return amount;
  }

  return 0;
}

export function calculateLaunchFuelCostFromGallons(
  gallons: number,
  listPricePerGallon: number,
  applyBoatClubDiscount: boolean
): number {
  if (gallons <= 0 || listPricePerGallon <= 0) {
    return 0;
  }
  const effectivePrice = applyBoatClubDiscount
    ? getBoatClubLaunchFuelUnitPrice(listPricePerGallon)
    : listPricePerGallon;
  return gallons * effectivePrice;
}

export function calculateLaunchFuelCostFromDollars(
  dollars: number,
  listPricePerGallon: number,
  applyBoatClubDiscount: boolean
): number {
  if (dollars <= 0) {
    return 0;
  }
  if (!applyBoatClubDiscount || listPricePerGallon <= 0) {
    return dollars;
  }
  const gallons = dollars / listPricePerGallon;
  return calculateLaunchFuelCostFromGallons(gallons, listPricePerGallon, true);
}

export function estimateLaunchFuelGallons(input: {
  listPricePerGallon: number;
  mode: 'GALLONS' | 'DOLLARS' | 'FILL_TO_FULL';
  amount?: number | null;
  tankCapacityGallons?: number | null;
}): number {
  const { listPricePerGallon, mode, amount, tankCapacityGallons } = input;

  if (mode === 'FILL_TO_FULL') {
    return tankCapacityGallons != null && tankCapacityGallons > 0 ? tankCapacityGallons : 0;
  }

  if (amount != null && amount > 0) {
    if (mode === 'GALLONS') {
      return amount;
    }
    if (mode === 'DOLLARS' && listPricePerGallon > 0) {
      return amount / listPricePerGallon;
    }
  }

  return 0;
}

export function calculateLaunchFuelDiscountSavings(
  gallons: number,
  applyBoatClubDiscount: boolean
): number {
  if (!applyBoatClubDiscount || gallons <= 0) {
    return 0;
  }
  return gallons * BOAT_CLUB_LAUNCH_FUEL_DISCOUNT_PER_GALLON;
}

export function calculateLaunchFuelListEstimate(input: LaunchFuelEstimateInput): number {
  return calculateLaunchFuelEstimate({ ...input, applyBoatClubDiscount: false });
}
